export interface NamedEntity {
  readonly name: string;
  readonly character: string;
  readonly semicolon: 'optional' | 'required';
}

interface Digits {
  readonly value: number;
  readonly length: number;
}

interface Reference {
  readonly character: string;
  readonly length: number;
}

const AMPERSAND = '&';
const SEMICOLON = ';';
const NUMERIC_MARKER = '#';
const HEX_MARKER_LOWER = 'x';
const HEX_MARKER_UPPER = 'X';
const NUMERIC_PREFIX_LENGTH = 2;
const DECIMAL_RADIX = 10;
const HEX_RADIX = 16;
type Radix = typeof DECIMAL_RADIX | typeof HEX_RADIX;
/** U+10FFFF, U+D800 and U+DFFF in decimal: prettier and `unicorn/number-literal-case` disagree on hex digit case. */
const MAX_CODE_POINT = 1_114_111;
const SURROGATE_MIN = 55_296;
const SURROGATE_MAX = 57_343;

/**
 * The named set of the rule (C2), matched case-sensitively. In this rule only `lt`, `gt`, `amp` and `quot` (and their
 * uppercase forms) decode without a `;`; every other name needs it (browsers also decode more names, such as `&nbsp`,
 * without one).
 */
const NAMED_ENTITIES: readonly NamedEntity[] = [
  { name: 'lt', character: '<', semicolon: 'optional' },
  { name: 'gt', character: '>', semicolon: 'optional' },
  { name: 'amp', character: '&', semicolon: 'optional' },
  { name: 'quot', character: '"', semicolon: 'optional' },
  { name: 'LT', character: '<', semicolon: 'optional' },
  { name: 'GT', character: '>', semicolon: 'optional' },
  { name: 'AMP', character: '&', semicolon: 'optional' },
  { name: 'QUOT', character: '"', semicolon: 'optional' },
  { name: 'apos', character: "'", semicolon: 'required' },
  { name: 'sol', character: '/', semicolon: 'required' },
  { name: 'equals', character: '=', semicolon: 'required' },
  { name: 'colon', character: ':', semicolon: 'required' },
  { name: 'excl', character: '!', semicolon: 'required' },
  { name: 'quest', character: '?', semicolon: 'required' },
  { name: 'Tab', character: '\t', semicolon: 'required' },
  { name: 'NewLine', character: '\n', semicolon: 'required' },
  { name: 'nbsp', character: '\u00A0', semicolon: 'required' },
];

/**
 * Builds the reader of a named reference set. C2 reads the longest name first, so a name that starts another one
 * never shadows it, whatever order the set is declared in.
 *
 * Exported so the longest-name-first rule can be tested on a set whose declaration order is chosen by the test.
 *
 * @param entities The named set.
 * @returns A reader that takes the text and the position of an `&`, and returns the character and the raw length of
 * the longest name of the set that fits there, or `null` when none does.
 */
export function createNamedReferenceReader(
  entities: readonly NamedEntity[],
): (text: string, ampersand: number) => Reference | null {
  const longestFirst = entities.toSorted((first, second) => second.name.length - first.name.length);

  return (text, ampersand) => {
    const nameStart = ampersand + 1;

    for (const entity of longestFirst) {
      if (!text.startsWith(entity.name, nameStart)) {
        continue;
      }

      const nameEnd = nameStart + entity.name.length;
      const hasSemicolon = text[nameEnd] === SEMICOLON;
      if (hasSemicolon || entity.semicolon === 'optional') {
        return { character: entity.character, length: nameEnd + (hasSemicolon ? 1 : 0) - ampersand };
      }
    }
    return null;
  };
}

const namedReferenceAt = createNamedReferenceReader(NAMED_ENTITIES);

/**
 * Decodes the character references of the rule (C2) in one left-to-right pass: a decoded character goes to the
 * output and is never scanned again, so `&amp;lt;` is `&lt;` after one pass and `<` after two. It is the rule's
 * decoder only, not a general HTML decoder.
 *
 * Numeric references are `&#` or `&#x`, the longest run of digits (leading zeros allowed) and an optional `;`;
 * the value saturates past U+10FFFF. An empty run, 0, a surrogate and a value above U+10FFFF stay literal.
 * Named references are limited to the set in `NAMED_ENTITIES`. Anything else stays literal.
 *
 * It is hand-written, not `he` or `WebUtility.HtmlDecode`, because those libraries differ from each other in named
 * sets and `;` rules, so the client and the server would decode differently. It never throws.
 *
 * @param text The text to decode.
 * @returns The text with every recognised reference replaced by its character.
 */
export function decodeHtmlEntities(text: string): string {
  const parts: string[] = [];
  let literalStart = 0;

  for (let ampersand = text.indexOf(AMPERSAND); ampersand !== -1; ampersand = text.indexOf(AMPERSAND, ampersand + 1)) {
    const reference = referenceAt(text, ampersand);
    if (reference === null) {
      continue;
    }

    parts.push(text.slice(literalStart, ampersand), reference.character);
    literalStart = ampersand + reference.length;
  }

  parts.push(text.slice(literalStart));
  return parts.join('');
}

/**
 * Reads one ASCII digit of the radix.
 *
 * @param text The text being decoded.
 * @param index The position to read.
 * @param radix 10 or 16.
 * @returns The digit's value, or `null` when the character there is no digit of that radix.
 */
function digitValueAt(text: string, index: number, radix: Radix): number | null {
  const char = text[index];
  const isDigit = isDecimalDigit(char) || (radix === HEX_RADIX && isHexLetter(char));
  return isDigit ? Number.parseInt(char, radix) : null;
}

/**
 * Tells whether a character is an ASCII decimal digit; never a Unicode digit.
 *
 * @param char One character.
 * @returns Whether it is `0` to `9`.
 */
function isDecimalDigit(char: string): boolean {
  return char >= '0' && char <= '9';
}

/**
 * Tells whether a numeric reference may decode: not 0, not a surrogate and not above U+10FFFF.
 *
 * @param value The reference's value, saturated past U+10FFFF.
 * @returns Whether it decodes to a character.
 */
function isDecodableCodePoint(value: number): boolean {
  return value !== 0 && value <= MAX_CODE_POINT && (value < SURROGATE_MIN || value > SURROGATE_MAX);
}

/**
 * Tells whether a character is an ASCII hex letter; never a Unicode letter.
 *
 * @param char One character.
 * @returns Whether it is `A` to `F` or `a` to `f`.
 */
function isHexLetter(char: string): boolean {
  return (char >= 'A' && char <= 'F') || (char >= 'a' && char <= 'f');
}

/**
 * Tells whether a character is the `x` or `X` of a hex reference.
 *
 * @param char One character, or `undefined` past the end of the text.
 * @returns Whether it marks a hex reference.
 */
function isHexMarker(char: string | undefined): boolean {
  return char === HEX_MARKER_LOWER || char === HEX_MARKER_UPPER;
}

/**
 * Reads `&#` or `&#x`, the digits and an optional `;`.
 *
 * @param text The text being decoded.
 * @param ampersand The position of the `&`.
 * @returns The character and the raw length, or `null` for an empty run, 0, a surrogate or a value above U+10FFFF.
 */
function numericReferenceAt(text: string, ampersand: number): Reference | null {
  const isHex = isHexMarker(text[ampersand + NUMERIC_PREFIX_LENGTH]);
  const digitsStart = ampersand + NUMERIC_PREFIX_LENGTH + (isHex ? 1 : 0);
  const digits = readDigits(text, digitsStart, isHex ? HEX_RADIX : DECIMAL_RADIX);
  if (digits.length === 0 || !isDecodableCodePoint(digits.value)) {
    return null;
  }

  const digitsEnd = digitsStart + digits.length;
  const semicolonLength = text[digitsEnd] === SEMICOLON ? 1 : 0;
  return {
    character: String.fromCodePoint(digits.value),
    length: digitsEnd + semicolonLength - ampersand,
  };
}

/**
 * Reads the longest run of digits and its value, which stops growing once it passes U+10FFFF so a long run cannot
 * overflow.
 *
 * @param text The text being decoded.
 * @param from Where the run starts.
 * @param radix 10 or 16.
 * @returns The value, saturated at one past U+10FFFF, and the number of digits read.
 */
function readDigits(text: string, from: number, radix: Radix): Digits {
  const saturated = MAX_CODE_POINT + 1;
  let value = 0;
  let index = from;

  for (let digit = digitValueAt(text, index, radix); digit !== null; digit = digitValueAt(text, index, radix)) {
    value = Math.min(value * radix + digit, saturated);
    index += 1;
  }

  return { value, length: index - from };
}

/**
 * Reads the reference that starts at an `&`.
 *
 * @param text The text being decoded.
 * @param ampersand The position of the `&`.
 * @returns The decoded character and the raw length it covers, or `null` when the text there stays literal.
 */
function referenceAt(text: string, ampersand: number): Reference | null {
  return text[ampersand + 1] === NUMERIC_MARKER
    ? numericReferenceAt(text, ampersand)
    : namedReferenceAt(text, ampersand);
}
